import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Robot, GameController, Lightning, Play, ArrowLeft, Brain,
  ChatCircleDots, Trophy, Clock, X, PaperPlaneTilt, Lightbulb
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const GAMES = ['Chess', 'Catan', 'Magic: The Gathering', 'Risk', 'Ticket to Ride'];
const DIFFICULTIES = [
  { value: 'easy', label: 'Easy', desc: 'Cautious, makes mistakes', color: '#10B981' },
  { value: 'medium', label: 'Medium', desc: 'Balanced strategy', color: '#3B82F6' },
  { value: 'hard', label: 'Hard', desc: 'Aggressive & calculated', color: '#F59E0B' },
  { value: 'expert', label: 'Expert', desc: 'Ruthlessly optimal', color: '#EF4444' },
];

export default function BotPracticePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('play');
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedGame, setSelectedGame] = useState('Chess');
  const [selectedDiff, setSelectedDiff] = useState('medium');
  const [gameState, setGameState] = useState('');
  const [botThinking, setBotThinking] = useState(false);
  const [lastMove, setLastMove] = useState(null);
  const [advisorMode, setAdvisorMode] = useState(false);
  const [advisorQuestion, setAdvisorQuestion] = useState('');
  const [advice, setAdvice] = useState(null);

  const fetchSessions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/bots/practice`, { withCredentials: true });
      setSessions(r.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const startPractice = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/bots/practice`, {
        game_name: selectedGame, bot_difficulty: selectedDiff,
      }, { withCredentials: true });
      setActiveSession(r.data);
      toast.success(`Practice started vs ${r.data.bot_name}!`);
      setTab('session');
      fetchSessions();
    } catch (e) { toast.error('Failed to start practice'); }
    setLoading(false);
  };

  const requestBotMove = async () => {
    if (!activeSession) return;
    setBotThinking(true);
    setLastMove(null);
    try {
      let state = {};
      try { state = JSON.parse(gameState || '{}'); } catch { state = { description: gameState }; }
      const r = await axios.post(`${API}/api/bots/move`, {
        session_id: activeSession.id,
        game_state: state,
        context: `Turn ${activeSession.turn_number + 1}`,
      }, { withCredentials: true });
      setLastMove(r.data);
      setActiveSession(prev => ({ ...prev, turn_number: r.data.turn }));
    } catch (e) { toast.error('Bot failed to move'); }
    setBotThinking(false);
  };

  const endSession = async (result) => {
    if (!activeSession) return;
    try {
      await axios.post(`${API}/api/bots/practice/${activeSession.id}/end?result=${result}`, {}, { withCredentials: true });
      toast.success(`Practice ended: ${result}`);
      setActiveSession(null);
      setLastMove(null);
      setTab('play');
      fetchSessions();
    } catch (e) { toast.error('Failed'); }
  };

  const getAdvice = async () => {
    if (!advisorQuestion && !gameState) { toast.error('Enter game state or question'); return; }
    setBotThinking(true);
    setAdvice(null);
    try {
      let state = {};
      try { state = JSON.parse(gameState || '{}'); } catch { state = { description: gameState }; }
      const r = await axios.post(`${API}/api/bots/advisor`, {
        game_name: selectedGame, game_state: state,
        question: advisorQuestion || 'What should I do next?',
      }, { withCredentials: true });
      setAdvice(r.data);
    } catch (e) { toast.error('Advisor unavailable'); }
    setBotThinking(false);
  };

  const diffColor = DIFFICULTIES.find(d => d.value === selectedDiff)?.color || '#3B82F6';

  return (
    <div className="max-w-3xl mx-auto" data-testid="bot-practice-page">
      <button onClick={() => navigate('/home')} className="flex items-center gap-1 text-[12px] text-[#555] hover:text-white mb-4 transition-colors">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>AI Game Bots</h1>
          <p className="text-[13px] text-[#444] mt-0.5">Practice against AI or get move advice</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-[#0c0c14] rounded-lg p-1 w-fit border border-white/[0.06]">
        {[
          { id: 'play', label: 'Practice', icon: Robot },
          { id: 'advisor', label: 'AI Advisor', icon: Lightbulb },
          { id: 'history', label: 'History', icon: Clock },
        ].map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setAdvisorMode(t.id === 'advisor'); }}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[12px] font-medium transition-colors ${
              tab === t.id ? 'bg-[#9D4CDD]/15 text-[#9D4CDD]' : 'text-[#555] hover:text-[#999]'
            }`} data-testid={`tab-${t.id}`}>
            <t.icon size={13} weight={tab === t.id ? 'duotone' : 'regular'} /> {t.label}
          </button>
        ))}
      </div>

      {/* Practice Setup */}
      {tab === 'play' && !activeSession && (
        <div className="space-y-4">
          <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
            <h2 className="text-[14px] font-bold text-white mb-4 flex items-center gap-2">
              <GameController size={16} weight="duotone" className="text-[#9D4CDD]" /> Select Game
            </h2>
            <div className="flex flex-wrap gap-2">
              {GAMES.map(g => (
                <button key={g} onClick={() => setSelectedGame(g)}
                  className={`px-4 py-2 rounded-lg text-[12px] font-medium border transition-colors ${
                    selectedGame === g ? 'bg-[#9D4CDD]/15 border-[#9D4CDD]/30 text-[#9D4CDD]' : 'bg-white/[0.02] border-white/[0.06] text-[#666] hover:text-white'
                  }`} data-testid={`game-${g.replace(/[^a-z]/gi, '')}`}>
                  {g}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
            <h2 className="text-[14px] font-bold text-white mb-4 flex items-center gap-2">
              <Lightning size={16} weight="duotone" className="text-[#F59E0B]" /> Difficulty
            </h2>
            <div className="grid grid-cols-2 gap-2">
              {DIFFICULTIES.map(d => (
                <button key={d.value} onClick={() => setSelectedDiff(d.value)}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    selectedDiff === d.value ? `border-opacity-40` : 'border-white/[0.06] hover:border-white/[0.12]'
                  }`}
                  style={selectedDiff === d.value ? { borderColor: d.color, background: `${d.color}10` } : {}}
                  data-testid={`diff-${d.value}`}>
                  <p className="text-[13px] font-semibold" style={{ color: selectedDiff === d.value ? d.color : '#fff' }}>{d.label}</p>
                  <p className="text-[10px] text-[#555]">{d.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <Button onClick={startPractice} disabled={loading}
            className="w-full h-11 bg-[#9D4CDD] hover:bg-[#8B3DC9] text-white font-semibold text-[13px]"
            data-testid="start-practice-btn">
            <Play size={16} className="mr-2" /> {loading ? 'Starting...' : `Start Practice — ${selectedGame} vs ${selectedDiff.charAt(0).toUpperCase() + selectedDiff.slice(1)} Bot`}
          </Button>

          <p className="text-[10px] text-[#444] text-center">Practice matches don't affect your ratings or stats</p>
        </div>
      )}

      {/* Active Session */}
      {tab === 'play' && activeSession && (
        <div className="space-y-4" data-testid="active-practice-session">
          <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-[#9D4CDD]/10 flex items-center justify-center">
                  <Robot size={18} weight="duotone" className="text-[#9D4CDD]" />
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-white">vs {activeSession.bot_name}</p>
                  <p className="text-[10px] text-[#555]">{activeSession.game_name} | Turn {activeSession.turn_number}</p>
                </div>
              </div>
              <span className="text-[9px] uppercase px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20 font-medium">
                {activeSession.status}
              </span>
            </div>

            <div className="mb-3">
              <label className="text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1.5 block">Game State (JSON or description)</label>
              <textarea value={gameState} onChange={e => setGameState(e.target.value)}
                className="w-full bg-[#07070c] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[12px] text-white font-mono focus:border-[#9D4CDD]/40 focus:outline-none resize-none h-24 placeholder-[#2a2a2a]"
                placeholder='{"board": "...", "resources": {...}, "turn": 5}' data-testid="game-state-input" />
            </div>

            <div className="flex gap-2">
              <Button onClick={requestBotMove} disabled={botThinking}
                className="flex-1 h-9 bg-[#9D4CDD] hover:bg-[#8B3DC9] text-white text-[11px]"
                data-testid="request-move-btn">
                <Brain size={14} className="mr-1" /> {botThinking ? 'Thinking...' : 'Get Bot Move'}
              </Button>
              <Button variant="outline" onClick={() => endSession('win')}
                className="h-9 px-3 bg-transparent border-[#10B981]/30 text-[#10B981] text-[11px] hover:bg-[#10B981]/10" data-testid="end-win-btn">Win</Button>
              <Button variant="outline" onClick={() => endSession('loss')}
                className="h-9 px-3 bg-transparent border-[#EF4444]/30 text-[#EF4444] text-[11px] hover:bg-[#EF4444]/10" data-testid="end-loss-btn">Loss</Button>
              <Button variant="outline" onClick={() => endSession('abandoned')}
                className="h-9 px-3 bg-transparent border-white/[0.08] text-[#555] text-[11px]" data-testid="end-abandon-btn">Quit</Button>
            </div>
          </div>

          {/* Bot Response */}
          <AnimatePresence>
            {lastMove && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="bg-[#0c0c14] border border-[#9D4CDD]/20 rounded-xl p-5" data-testid="bot-move-response">
                <div className="flex items-center gap-2 mb-3">
                  <Robot size={16} weight="duotone" className="text-[#9D4CDD]" />
                  <span className="text-[13px] font-semibold text-white">{lastMove.bot_name}'s Move</span>
                  <span className="text-[10px] text-[#555]">Turn {lastMove.turn}</span>
                </div>
                <div className="bg-[#07070c] rounded-lg p-3 border border-white/[0.04] mb-2">
                  <p className="text-[14px] text-[#9D4CDD] font-mono font-bold">{lastMove.move}</p>
                  {lastMove.move_type && <p className="text-[10px] text-[#555] mt-1">Type: {lastMove.move_type}</p>}
                </div>
                {lastMove.reasoning && (
                  <p className="text-[12px] text-[#888]">{lastMove.reasoning}</p>
                )}
                {lastMove.confidence !== undefined && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[10px] text-[#555]">Confidence:</span>
                    <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                      <div className="h-full bg-[#9D4CDD] rounded-full transition-all" style={{ width: `${(lastMove.confidence || 0) * 100}%` }} />
                    </div>
                    <span className="text-[10px] text-[#9D4CDD]">{Math.round((lastMove.confidence || 0) * 100)}%</span>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {tab === 'session' && activeSession && (() => { setTab('play'); return null; })()}

      {/* AI Advisor */}
      {tab === 'advisor' && (
        <div className="space-y-4">
          <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
            <h2 className="text-[14px] font-bold text-white mb-4 flex items-center gap-2">
              <Lightbulb size={16} weight="duotone" className="text-[#F59E0B]" /> AI Move Advisor
            </h2>
            <p className="text-[12px] text-[#666] mb-4">Describe your game state and get strategic advice from AI</p>

            <div className="flex flex-wrap gap-2 mb-3">
              {GAMES.map(g => (
                <button key={g} onClick={() => setSelectedGame(g)}
                  className={`px-3 py-1.5 rounded-md text-[11px] font-medium border transition-colors ${
                    selectedGame === g ? 'bg-[#F59E0B]/15 border-[#F59E0B]/30 text-[#F59E0B]' : 'border-white/[0.06] text-[#555] hover:text-white'
                  }`}>{g}</button>
              ))}
            </div>

            <textarea value={gameState} onChange={e => setGameState(e.target.value)}
              className="w-full bg-[#07070c] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[12px] text-white font-mono focus:border-[#F59E0B]/40 focus:outline-none resize-none h-20 placeholder-[#2a2a2a] mb-2"
              placeholder="Describe your game state..." data-testid="advisor-state-input" />

            <input value={advisorQuestion} onChange={e => setAdvisorQuestion(e.target.value)}
              className="w-full bg-[#07070c] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[12px] text-white focus:border-[#F59E0B]/40 focus:outline-none placeholder-[#2a2a2a] mb-3"
              placeholder="What should I do next?" data-testid="advisor-question-input" />

            <Button onClick={getAdvice} disabled={botThinking}
              className="w-full h-9 bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold text-[12px]"
              data-testid="get-advice-btn">
              <Lightbulb size={14} className="mr-1" /> {botThinking ? 'Analyzing...' : 'Get Advice'}
            </Button>
          </div>

          <AnimatePresence>
            {advice && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className="bg-[#0c0c14] border border-[#F59E0B]/20 rounded-xl p-5" data-testid="advisor-response">
                {advice.overall_assessment && (
                  <p className="text-[12px] text-[#888] mb-3 italic">{advice.overall_assessment}</p>
                )}
                <div className="space-y-2">
                  {advice.suggestions?.map((s, i) => (
                    <div key={i} className="bg-[#07070c] rounded-lg p-3 border border-white/[0.04]">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[13px] font-semibold text-[#F59E0B]">{i + 1}. {s.move}</span>
                        <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-full font-medium ${
                          s.risk === 'low' ? 'bg-[#10B981]/10 text-[#10B981]' :
                          s.risk === 'high' ? 'bg-[#EF4444]/10 text-[#EF4444]' :
                          'bg-[#F59E0B]/10 text-[#F59E0B]'
                        }`}>{s.risk} risk</span>
                      </div>
                      <p className="text-[11px] text-[#888]">{s.reasoning}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* History */}
      {tab === 'history' && (
        <div className="space-y-2">
          {sessions.length === 0 ? (
            <div className="text-center py-12 text-[#444]">
              <Robot size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-[13px]">No practice sessions yet</p>
            </div>
          ) : sessions.map(s => (
            <div key={s.id} className="flex items-center gap-3 bg-[#0c0c14] border border-white/[0.06] rounded-lg px-4 py-3"
              data-testid={`session-${s.id}`}>
              <Robot size={16} weight="duotone" className="text-[#9D4CDD]" />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] text-white font-medium">{s.game_name} vs {s.bot_name}</p>
                <p className="text-[10px] text-[#555]">{s.bot_difficulty} | {s.turn_number} turns | {new Date(s.created_at).toLocaleDateString()}</p>
              </div>
              <span className={`text-[9px] uppercase px-2 py-0.5 rounded-full font-medium ${
                s.result === 'win' ? 'bg-[#10B981]/10 text-[#10B981]' :
                s.result === 'loss' ? 'bg-[#EF4444]/10 text-[#EF4444]' :
                s.status === 'active' ? 'bg-[#3B82F6]/10 text-[#3B82F6]' :
                'bg-white/5 text-[#555]'
              }`}>{s.result || s.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
